import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { AUTH_TOKEN_STORAGE_KEY, AUTH_ROLE_STORAGE_KEY, AUTH_NEEDS_BROKERAGE_STORAGE_KEY, AUTH_DEVELOPER_STORAGE_KEY } from '@/utils/authStorage'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue')
  },
  {
    path: '/',
    name: 'home',
    component: () => import('../views/DashboardView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/details',
    name: 'details',
    component: () => import('../views/DetailsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('../views/ClientsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/clients/:clientId',
    name: 'client-detail',
    component: () => import('../views/ClientDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/hernuwingsbrief',
    name: 'hernuwingsbrief',
    component: () => import('../views/RenewalLetterView.vue'),
    meta: { requiresAuth: true }
  },
  {
    // "Hernuwing (nuut)": a separate renewal flow being tested next to /hernuwingsbrief
    path: '/hernuwing',
    name: 'hernuwing',
    component: () => import('../views/HernuwingView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/kwotasies',
    name: 'kwotasies',
    component: () => import('../views/KwotasiesView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/eise',
    name: 'eise',
    component: () => import('../views/EiseView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/gebruikers',
    name: 'gebruikers',
    component: () => import('../views/GebruikersView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/instellings',
    name: 'instellings',
    component: () => import('../views/SettingsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/duck-verslae',
    name: 'duck-verslae',
    component: () => import('../duck/DuckReportsView.vue'),
    meta: { requiresAuth: true, requiresDeveloper: true }
  },
  {
    path: '/parser-terugvoer',
    name: 'parser-terugvoer',
    component: () => import('../views/ParserFeedbackView.vue'),
    meta: { requiresAuth: true, requiresDeveloper: true }
  },
  {
    path: '/verkope',
    name: 'verkope',
    component: () => import('../views/VerkopeView.vue'),
    meta: { requiresAuth: true, requiresDeveloper: true }
  },
  {
    path: '/skedule-probleme',
    name: 'skedule-probleme',
    component: () => import('../views/ScheduleProblemsView.vue'),
    meta: { requiresAuth: true, requiresDeveloper: true }
  },
  {
    path: '/wysigings',
    name: 'wysigings',
    component: () => import('../views/WysigingsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/registers/kansellasies',
    name: 'registers-kansellasies',
    component: () => import('../views/RegisterGekanselleerView.vue'),
    meta: { requiresAuth: true }
  },
  {
    // Forgot / reset password: public pages (no login), the reset one is opened from the mail
    path: '/vergeet-wagwoord',
    name: 'vergeet-wagwoord',
    component: () => import('../views/ForgotPasswordView.vue')
  },
  {
    path: '/herstel-wagwoord',
    name: 'herstel-wagwoord',
    component: () => import('../views/ResetPasswordView.vue')
  },
  {
    // The client's signing link: public (no login). Guarded by the link token and an ID check on the server.
    path: '/teken/:token',
    name: 'teken',
    component: () => import('../views/SignView.vue')
  },
  {
    path: '/rekords-van-advies/:recordId',
    name: 'rekord-van-advies',
    component: () => import('../views/RekordVanAdviesView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/registers/rekords-van-advies',
    name: 'registers-rekords-van-advies',
    component: () => import('../views/RegisterRekordsVanAdviesView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/rekords-nodig',
    name: 'rekords-nodig',
    component: () => import('../views/RekordsNodigView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/kies-makelaar',
    name: 'kies-makelaar',
    component: () => import('../views/BrokerageSelectView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/makelaars',
    name: 'makelaars',
    component: () => import('../views/BrokeragesView.vue'),
    meta: { requiresAuth: true, requiresDeveloper: true }
  },
  {
    path: '/abonnement',
    name: 'abonnement',
    component: () => import('../views/AbonnementView.vue'),
    meta: { requiresAuth: true }
  },
  {
    // Back from Paystack after signing up on the sales website: public (the visitor has no account until this payment is confirmed).
    path: '/inskrywing/klaar',
    name: 'inskrywing-klaar',
    component: () => import('../views/SignupDoneView.vue')
  },
  {
    path: '/intekenare',
    name: 'intekenare',
    component: () => import('../views/IntekenaarsView.vue'),
    meta: { requiresAuth: true, requiresDeveloper: true }
  },
  {
    path: '/registers/nuwe-aansoeke',
    name: 'registers-nuwe-aansoeke',
    component: () => import('../views/RegisterNuweAansoekeView.vue'),
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
  const hasToken = !!localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

  if (requiresAuth && !hasToken) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (hasToken && localStorage.getItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY) === 'true'
      && to.name !== 'kies-makelaar' && to.name !== 'login' && to.name !== 'teken' && to.name !== 'inskrywing-klaar') {
    return { name: 'kies-makelaar', query: { redirect: to.fullPath } };
  }

  if (to.matched.some((record) => record.meta.requiresDeveloper)
      && localStorage.getItem(AUTH_DEVELOPER_STORAGE_KEY) !== 'true') {
    return { name: 'home' };
  }

  if (requiresAdmin && localStorage.getItem(AUTH_ROLE_STORAGE_KEY) !== 'ADMIN') {
    return { name: 'home' };
  }

  if (to.name === 'login' && hasToken) {
    return { name: 'home' };
  }

  return true;
})

export default router
